"use client";

import type { ReactNode } from "react";

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useCarousel } from "@/components/detailed-listing-view/carousel/use-carousel";
import { ImageGalleryModal, type GalleryImage } from "@/components/chat/image-gallery-modal";
import { useHeicImage } from "@/hooks/use-heic-image";
import { Skeleton } from "@/components/ui/skeleton";
import { ImageCountBadge } from "@/components/ui/image-count-badge";
import { isHeicUrl } from "@/lib/image-url-helper";
import {
  galleryBackdropUrl,
  gallerySlideUrl,
  galleryThumbUrl,
} from "@/lib/listings/gallery-image-urls";

interface MobileListingGalleryProps {
  images: string[];
  title: string;
  isScheduled?: boolean;
  children?: ReactNode;
}

function GallerySlide({
  src,
  alt,
  isScheduled,
  priority,
  resolvedDisplaySrc,
}: {
  src: string;
  alt: string;
  isScheduled: boolean;
  priority: boolean;
  resolvedDisplaySrc?: string;
}) {
  const useParentSrc = resolvedDisplaySrc != null && resolvedDisplaySrc.length > 0;
  const { displaySrc, isConverting } = useHeicImage(src, { enabled: !useParentSrc });
  const resolvedSrc = useParentSrc ? resolvedDisplaySrc : displaySrc || src || "/placeholder.svg";
  const converting = useParentSrc ? false : isConverting;
  // Already sized by the CDN (or a local `blob:` the optimizer can't read
  // either way) -> `unoptimized` is unconditional, no `/_next/image` hop.
  const slideSrc = gallerySlideUrl(resolvedSrc);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
  }, [slideSrc]);

  const showImage = !converting && imageLoaded;

  return (
    <div className="relative h-full w-full shrink-0">
      {converting || !imageLoaded ? <Skeleton className="absolute inset-0" /> : null}
      <Image
        src={slideSrc}
        alt={alt}
        fill
        priority={priority}
        unoptimized
        className={`object-contain ${isScheduled ? "blur-md brightness-75" : ""} ${
          showImage ? "opacity-100" : "opacity-0"
        }`}
        draggable={false}
        onLoad={() => setImageLoaded(true)}
      />
    </div>
  );
}

function ThumbnailImage({
  image,
  title,
  index,
  isActive,
  isScheduled,
  onClick,
  disabled,
  enableConversion,
}: {
  image: string;
  title: string;
  index: number;
  isActive: boolean;
  isScheduled: boolean;
  onClick: () => void;
  disabled: boolean;
  enableConversion: boolean;
}) {
  const isHeic = isHeicUrl(image || "");
  const { displaySrc, isConverting } = useHeicImage(image || "", { enabled: enableConversion });
  const resolvedSrc = enableConversion
    ? displaySrc || image || "/placeholder.svg"
    : isHeic
      ? "/placeholder.svg"
      : image || "/placeholder.svg";
  // Same URL the desktop sibling's strip requests. This one was already small
  // (`/_next/image` at `sizes="64px"`), so the win here is not raw bytes but
  // dropping the optimizer hop AND converging with desktop: both strips mount
  // on every pageview, so identical URLs mean the second one is free.
  // `blob:` (HEIC output) passes through untouched, which is why `unoptimized`
  // below is unconditional.
  const thumbSrc = galleryThumbUrl(resolvedSrc);

  return (
    <button
      type="button"
      className={`relative h-16 w-16 shrink-0 rounded-md transition-all focus:outline-none ${
        isActive
          ? "z-20 border-2 border-white ring-2 ring-black"
          : "z-0 border-2 border-transparent"
      }`}
      onClick={onClick}
      disabled={disabled}
      aria-label={`View image ${index + 1}`}
    >
      <span className="pointer-events-none absolute inset-0 block overflow-hidden rounded-sm">
        {isConverting ? (
          <Skeleton className="absolute inset-0" />
        ) : (
          <Image
            src={thumbSrc}
            alt={`${title} - view ${index + 1}`}
            fill
            unoptimized
            className={`object-cover transition-all duration-300 ${isScheduled ? "blur-xs brightness-75" : ""}`}
            draggable={false}
          />
        )}
      </span>
    </button>
  );
}

export default function MobileListingGallery({
  images,
  title,
  isScheduled = false,
  children,
}: MobileListingGalleryProps) {
  const slides = useMemo(() => (images.length > 0 ? images : ["/placeholder.svg"]), [images]);
  const totalSlides = slides.length;
  const canDrag = slides.length > 1;
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [disableSlideTransition, setDisableSlideTransition] = useState(false);
  const [mountedSlides, setMountedSlides] = useState<Set<number>>(() => new Set([0]));
  const {
    currentSlide,
    setCurrentSlide,
    isDragging,
    dragOffset,
    carouselRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleWheel,
  } = useCarousel(0, totalSlides);

  const activeImage = slides[currentSlide] || slides[0];
  const { displaySrc: activeDisplaySrc } = useHeicImage(activeImage || "");
  const resolvedActiveSrc = activeDisplaySrc || activeImage || "/placeholder.svg";
  // Blurred backdrop only — points at the EXACT URL the feed card already
  // requested so arriving from the feed paints it out of the browser HTTP
  // cache instead of re-downloading. See `galleryBackdropUrl`.
  const backdropSrc = galleryBackdropUrl(resolvedActiveSrc);

  const selectSlide = useCallback(
    (index: number) => {
      if (index < 0 || index >= totalSlides) return;
      setDisableSlideTransition(true);
      setMountedSlides((prev) => {
        const next = new Set(prev);
        next.add(index);
        const start = Math.max(0, index - 1);
        const end = Math.min(totalSlides - 1, index + 1);
        for (let i = start; i <= end; i += 1) next.add(i);
        return next;
      });
      setCurrentSlide(index);
    },
    [setCurrentSlide, totalSlides],
  );

  useEffect(() => {
    setMountedSlides((prev) => {
      const next = new Set(prev);
      const start = Math.max(0, currentSlide - 1);
      const end = Math.min(totalSlides - 1, currentSlide + 1);
      for (let i = start; i <= end; i += 1) next.add(i);
      return next;
    });
  }, [currentSlide, totalSlides]);

  useLayoutEffect(() => {
    if (disableSlideTransition) {
      setDisableSlideTransition(false);
    }
  }, [currentSlide, disableSlideTransition]);

  const mountedSlideIndices = useMemo(
    () => Array.from(mountedSlides).sort((a, b) => a - b),
    [mountedSlides],
  );

  /**
   * `url` stays the full original — the fullscreen viewer is a pinch-zoom
   * surface and must have the real pixels (handoff §11).
   *
   * `previewUrl` is what `GallerySlide` above painted for the same photo, built
   * by the SAME `gallerySlideUrl` call, so the two strings cannot drift: the
   * slide asks for `gallerySlideUrl(resolvedSrc)` where `resolvedSrc` is the
   * `useHeicImage` output, and `toSupabasePublicImageThumbUrl` resolves its
   * input first with an idempotent resolver — so `gallerySlideUrl(url)` and
   * `gallerySlideUrl(resolveStorageUrl(url))` are the same string. Tapping the
   * slide therefore paints instantly out of the browser cache for 0 bytes
   * instead of showing a spinner over a fresh original.
   *
   * HEIC is excluded: for those the slide painted a `blob:` from an in-memory
   * transcode, which no CDN preset can reproduce. `gallerySlideUrl` on the raw
   * `.heic` would invent a URL nobody has ever requested and imgproxy cannot
   * decode. The modal guards this too; stated here as well because this is
   * where the knowledge of the raw reference lives.
   */
  const galleryImages = useMemo<GalleryImage[]>(
    () =>
      images.map((url, idx) => ({
        id: `img-${idx}`,
        url,
        alt: `${title} photo ${idx + 1}`,
        previewUrl: isHeicUrl(url) ? undefined : gallerySlideUrl(url),
      })),
    [images, title],
  );

  useEffect(() => {
    if (currentSlide > totalSlides - 1) {
      setCurrentSlide(0);
    }
  }, [currentSlide, setCurrentSlide, totalSlides]);

  return (
    <div className="w-full">
      <div className="relative w-full overflow-hidden bg-black">
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: "4/5" }}>
          <div className="absolute inset-0 overflow-hidden">
            {activeImage ? (
              <Image
                src={backdropSrc}
                alt=""
                fill
                unoptimized
                className={`object-cover blur-2xl scale-110 ${
                  isScheduled ? "opacity-40" : "opacity-60"
                }`}
              />
            ) : (
              <div className="h-full w-full bg-muted" />
            )}
            <div className="absolute inset-0 bg-black/40" />
          </div>

          <div
            ref={carouselRef}
            className={`absolute inset-0 overflow-hidden ${isScheduled ? "cursor-default" : canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
            onTouchStart={
              canDrag
                ? (e) => {
                    suppressClickRef.current = false;
                    pointerStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                    handleTouchStart(e);
                  }
                : undefined
            }
            onTouchMove={
              canDrag
                ? (e) => {
                    const start = pointerStartRef.current;
                    if (start) {
                      const dx = e.touches[0].clientX - start.x;
                      const dy = e.touches[0].clientY - start.y;
                      if (Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
                        suppressClickRef.current = true;
                      }
                    }
                    handleTouchMove(e);
                  }
                : undefined
            }
            onTouchEnd={
              canDrag
                ? () => {
                    handleTouchEnd();
                    pointerStartRef.current = null;
                  }
                : undefined
            }
            onMouseDown={
              canDrag
                ? (e) => {
                    suppressClickRef.current = false;
                    pointerStartRef.current = { x: e.clientX, y: e.clientY };
                    handleMouseDown(e);
                  }
                : undefined
            }
            onMouseMove={
              canDrag
                ? (e) => {
                    const start = pointerStartRef.current;
                    if (start) {
                      const dx = e.clientX - start.x;
                      const dy = e.clientY - start.y;
                      if (Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
                        suppressClickRef.current = true;
                      }
                    }
                    handleMouseMove(e);
                  }
                : undefined
            }
            onMouseUp={
              canDrag
                ? () => {
                    handleMouseUp();
                    pointerStartRef.current = null;
                  }
                : undefined
            }
            onMouseLeave={
              canDrag
                ? () => {
                    handleMouseLeave();
                    pointerStartRef.current = null;
                  }
                : undefined
            }
            onWheel={canDrag ? handleWheel : undefined}
            onClick={() => {
              if (images.length === 0 || isScheduled) return;
              if (suppressClickRef.current) {
                suppressClickRef.current = false;
                return;
              }
              setIsGalleryOpen(true);
            }}
          >
            <div className="absolute inset-0">
              {mountedSlideIndices.map((idx) => (
                <div
                  key={`slide-${idx}`}
                  className="absolute inset-0"
                  style={{
                    transform: isDragging
                      ? `translateX(calc(${(idx - currentSlide) * 100}% + ${dragOffset}px))`
                      : `translateX(${(idx - currentSlide) * 100}%)`,
                    transition: isDragging || disableSlideTransition ? "none" : "transform 0.25s ease-out",
                  }}
                >
                  <GallerySlide
                    src={slides[idx]}
                    alt={`${title} photo ${idx + 1}`}
                    isScheduled={isScheduled}
                    priority={idx === 0}
                    resolvedDisplaySrc={idx === currentSlide ? resolvedActiveSrc : undefined}
                  />
                </div>
              ))}
            </div>
          </div>

          {children}

          {images.length > 0 ? (
            <ImageCountBadge
              className="absolute left-3 bottom-3 z-30"
              current={currentSlide + 1}
              total={images.length}
            />
          ) : null}

        </div>
      </div>

      {images.length > 1 ? (
        <div className="relative z-10 flex gap-2 overflow-x-auto px-4 py-3.5 scrollbar-hide">
          {images.map((image, index) => (
            <ThumbnailImage
              key={index}
              image={image}
              title={title}
              index={index}
              isActive={currentSlide === index}
              isScheduled={isScheduled}
              onClick={() => selectSlide(index)}
              disabled={false}
              enableConversion
            />
          ))}
        </div>
      ) : null}

      {images.length > 0 ? (
        <ImageGalleryModal
          images={galleryImages}
          isOpen={isGalleryOpen}
          onCloseAction={() => setIsGalleryOpen(false)}
          initialIndex={currentSlide}
        />
      ) : null}
    </div>
  );
}
